/**
 * COLLOCAZIONE IN BACHECA — la cascata pipeline → stato per la scheda di un'entità (TS-117).
 *
 * <b>È il punto d'ingresso di tutto il meccanismo kanban, e senza di esso una board resta vuota per
 * sempre.</b> Una board mostra solo i record che hanno una riga di collocazione in uno degli stati
 * della sua pipeline, e `bootstrapUnassigned` non ne scrive nessuna — scelta voluta, perché
 * collocare d'ufficio un'intera anagrafica scriverebbe una riga per ogni record mai importato.
 * Qualcuno deve metterci il primo record, e quel qualcuno è questa cascata.
 *
 * <b>Perché la collocazione sta nella scheda e non sulla board.</b> Perché è <b>una sola</b>:
 * l'indice `G_CN_CP` è unico su (groupId, classNameId, classPK), quindi ogni record ha al più uno
 * stato, in una sola pipeline. È una proprietà del record, e le proprietà del record si leggono e si
 * scrivono dove sta il record. Lo stesso record compare poi su <b>tutte</b> le board della sua
 * pipeline — sono più viste della stessa collocazione, distinte dal filtro — e spostarlo da una le
 * aggiorna tutte, perché la colonna è una.
 *
 * <b>Due tendine e non una.</b> La pipeline NON si salva: è solo il filtro della seconda, e si parte
 * da quella che possiede lo stato attuale. Lo stato sì. Le due sono legate — cambiando pipeline lo
 * stato scelto prima non esiste più — quindi cambiare la prima azzera la collocazione e la
 * <b>scrive subito</b>: lasciare a video uno stato dell'altra pipeline direbbe una cosa che il
 * database non dice.
 *
 * <b>Non è il campo della scheda preventivo.</b> Quello edita lo stato solo DENTRO la pipeline che
 * già possiede il record, e per un record non collocato resta in sola lettura, perché lì
 * l'assegnazione si fa dal form. Contatti e aziende quel campo nel form non ce l'hanno, quindi qui
 * serve la cascata piena o non si entra in board da nessuna parte.
 *
 * <b>Non renderizza niente se non ci sono pipeline di quel tipo</b>, e non un campo disabilitato: un
 * campo disabilitato chiede di chiedersi perché.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { InfoLine } from './InfoLine';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { pipelinesOfType } from '../entities/pipeline/pipelineEntityTypes';
import { fetchPipelines } from '../entities/pipeline/pipelineThunks';
import type { PipelineSliceState } from '../entities/pipeline/pipelineTypes';
import {
  findOwningPipeline,
  pipelineStateLabel,
  sortedStates,
} from '../entities/pipeline/pipelineUtils';

interface PipelinePlacementProps {
  /** FQN del tipo: serve a scartare le pipeline di ALTRE entità, che stanno nello stesso slice. */
  fqn: string | undefined;
  /** Stato corrente del record. 0 = non collocato. */
  currentStateId: number;
  /** Scrive la collocazione. Riceve 0 per toglierla. */
  onCommit: (pipelineStateId: number) => Promise<void>;
}

export function PipelinePlacement({ fqn, currentStateId, onCommit }: PipelinePlacementProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { items: allPipelines } = useAppSelector((s) => s.pipelines as PipelineSliceState);

  useEffect(() => {
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  // SOLO le pipeline di questo tipo. Lo slice è condiviso: senza il filtro `findOwningPipeline`
  // potrebbe attribuire il record a un funnel che non è suo, se due stati di entità diverse
  // avessero lo stesso id.
  const pipelines = pipelinesOfType(allPipelines, fqn);
  const owning = findOwningPipeline(pipelines, currentStateId);

  // Quale pipeline mostra la prima tendina. Parte da quella che possiede lo stato attuale; da lì in
  // poi comanda la scelta dell'utente, anche prima che abbia scelto uno stato.
  const [pipelineId, setPipelineId] = useState(0);

  useEffect(() => {
    setPipelineId(owning?.pipelineId ?? 0);
  }, [owning?.pipelineId]);

  if (pipelines.length === 0) return null;

  const selected = pipelines.find((p) => p.pipelineId === pipelineId);

  return (
    <>
      <InfoLine label={t('pipeline.placementLabel')}>
        <TextField
          select
          size="small"
          fullWidth
          value={pipelineId || ''}
          onChange={(e) => {
            const next = e.target.value === '' ? 0 : Number(e.target.value);

            setPipelineId(next);

            // Cambiare pipeline TOGLIE la collocazione, e la scrive subito.
            if (currentStateId) void onCommit(0);
          }}
        >
          <MenuItem value="">{t('common.none')}</MenuItem>
          {pipelines.map((p) => (
            <MenuItem key={p.pipelineId} value={p.pipelineId}>{p.name}</MenuItem>
          ))}
        </TextField>
      </InfoLine>

      <InfoLine label={t('pipeline.placementState')}>
        <TextField
          select
          size="small"
          fullWidth
          value={currentStateId || ''}
          disabled={pipelineId <= 0}
          onChange={(e) => void onCommit(e.target.value === '' ? 0 : Number(e.target.value))}
        >
          <MenuItem value="">{t('common.none')}</MenuItem>
          {sortedStates(selected).map((st) => (
            <MenuItem key={st.pipelineStateId} value={st.pipelineStateId}>
              {pipelineStateLabel(st)}
            </MenuItem>
          ))}
        </TextField>
      </InfoLine>
    </>
  );
}
